import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// Direct imports (not the barrel): keeps `@mui/x-date-pickers` out of the bundle.
import { FilterBar } from '../../../components/filters/FilterBar';
import { SearchInput } from '../../../components/filters/SearchInput';
import {
  ON_SALE_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
  type CatalogFilters,
} from '../_lib/catalogFilters';

/**
 * Filter toolbar for `/vehicle-catalog` (AUT-437): search + Статус +
 * В продаже + Источник. Values live in the URL via the parent's `setFilters`;
 * a default value removes its param.
 */
const MIN_SEARCH_LENGTH = 1;

type Patch = Partial<Record<keyof CatalogFilters, string | null>>;

export interface CatalogFilterBarProps {
  filters: CatalogFilters;
  setFilters: (patch: Patch) => void;
  canClear: boolean;
  onClear: () => void;
}

const Select = ({
  label,
  value,
  options,
  labelOf,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  labelOf: (option: string) => string;
  onChange: (value: string | null) => void;
}): ReactElement => (
  <TextField
    select
    size="small"
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value === options[0] ? null : e.target.value)}
    sx={{ minWidth: 160 }}
  >
    {options.map((o) => (
      <MenuItem key={o} value={o}>
        {labelOf(o)}
      </MenuItem>
    ))}
  </TextField>
);

export const CatalogFilterBar = ({
  filters,
  setFilters,
  canClear,
  onClear,
}: CatalogFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <SearchInput
        initialValue={filters.q ?? ''}
        placeholder={t('vehicleCatalog.search.placeholder')}
        minLength={MIN_SEARCH_LENGTH}
        onSearch={(q) => setFilters({ q: q || null })}
      />
      <Select
        label={t('vehicleCatalog.filters.statusLabel')}
        value={filters.status}
        options={STATUS_OPTIONS}
        labelOf={(o) => t(`vehicleCatalog.filters.status.${o}`)}
        onChange={(status) => setFilters({ status })}
      />
      <Select
        label={t('vehicleCatalog.filters.onSaleLabel')}
        value={filters.onSale}
        options={ON_SALE_OPTIONS}
        labelOf={(o) => t(`vehicleCatalog.filters.onSale.${o}`)}
        onChange={(onSale) => setFilters({ onSale })}
      />
      <Select
        label={t('vehicleCatalog.filters.sourceLabel')}
        value={filters.source}
        options={SOURCE_OPTIONS}
        labelOf={(o) =>
          o === 'all' ? t('vehicleCatalog.filters.source.all') : t(`vehicleCatalog.source.${o}`)
        }
        onChange={(source) => setFilters({ source })}
      />
    </FilterBar>
  );
};
